import type { ValidateAddressResponse } from '@/integrations/Google/AddressValidationApi/types';
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { ImmersveError } from '@/common/ImmersveError';
import { config } from '@/config';

export class GoogleValidationApiClient {
  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: 'https://addressvalidation.googleapis.com',
    });

    this.axiosInstance.defaults.headers.post['Content-Type'] = 'application/json';
    this.axiosInstance.defaults.headers.put['Content-Type'] = 'application/json';

    axiosRetry(this.axiosInstance, {
      retries: 2,
      retryCondition: (e) => {
        return isNetworkOrIdempotentRequestError(e) || !e.status || !e.response?.status;
      },
      retryDelay: axiosRetry.exponentialDelay, // delay * 0.2 * Math.random(), initial delay is 100ms, then exponentially increases
    });

    if (!config.googleApiKey) {
      throw ImmersveError.create({
        name: 'GoogleValidationApiKeyNotSet',
        message: 'Google Validation API key has not set in the configuration',
      });
    }
  }

  private request<T>(request: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.request<T>(request);
  }

  async validateAddress({
    address,
    requiredCountryCode,
  }: {
    address: string;
    requiredCountryCode: string;
  }): Promise<ValidateAddressResponse> {
    try {
      const response = await this.request<ValidateAddressResponse>({
        url: `/v1:validateAddress?key=${config.googleApiKey}`,
        method: 'POST',
        data: {
          address: {
            regionCode: requiredCountryCode,
            addressLines: [address],
          },
        },
      });
      return response.data;
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.data?.error?.message?.includes('Address too long')
      ) {
        throw ImmersveError.create({
          name: 'GoogleAddressValidateTooLong',
          message: 'Address too long in length.',
        });
      }

      throw ImmersveError.fromCause({
        name: 'GoogleAddressValidateApiFailed',
        cause: err as Error,
      });
    }
  }

  static create(): GoogleValidationApiClient {
    return new GoogleValidationApiClient();
  }
}
